import { useSuspenseQuery } from '@tanstack/react-query';
import { router } from 'expo-router';
import React, { useCallback, useRef, useState } from 'react';

import { getBarberByIdOrRouteQuery } from '@/api/booking/getBarberByIdOrRouteQuery';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { ProfessionalPasscodeBottomSheet } from '@/components/booking/ProfessionalPasscodeBottomSheet';
import { routes } from '@/constants/routes';
import {
  addToPasscodeCache,
  isBarberPasscodeValidated,
} from '@/hooks/auth/usePasscodeProtector';

import type { PasscodeGateProps } from './types';

export function PasscodeGate({
  shopId,
  barberId,
  children,
  fallback,
  promoId,
  cart,
}: PasscodeGateProps) {
  const { data: shop } = useSuspenseQuery(shopDetailQuery(shopId));
  const { data: professional } = useSuspenseQuery(
    getBarberByIdOrRouteQuery(shop.id, barberId),
  );

  const [isValidated, setIsValidated] = useState(() =>
    isBarberPasscodeValidated(professional.barber.id),
  );

  const isSuccessRef = useRef(false);

  const passcodeRequired =
    shop.bookingCodeEnabled && !!professional.barber.requirePasscode;

  const handleSuccess = useCallback(
    (passcode: string) => {
      isSuccessRef.current = true;
      addToPasscodeCache(professional.barber.id, passcode);
      setIsValidated(true);
    },
    [professional.barber.id],
  );

  const handleCancelSafe = useCallback(() => {
    if (!isSuccessRef.current) {
      router.dismissTo(
        routes.chooseProfessional({
          promoId: promoId,
          shopRoute: shopId as string,
          cart: cart,
        }),
      );
    }
  }, [shopId, promoId, cart]);

  if (!passcodeRequired || isValidated) {
    return <>{children}</>;
  }

  return (
    <>
      {fallback}
      <ProfessionalPasscodeBottomSheet
        professional={professional}
        isOpen
        onSuccess={handleSuccess}
        onCancel={handleCancelSafe}
      />
    </>
  );
}

PasscodeGate.displayName = 'PasscodeGate';
